import React, { useEffect, useState } from "react";
import {
  TextField,
  Button,
  Alert,
  Stack,
  LinearProgress,
  InputAdornment,
  IconButton,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useNavigate, useSearchParams, Link as RouterLink } from "react-router-dom";
import ClientPortalShell from "./ClientPortalShell";
import {
  portalValidatePasswordToken,
  portalCompletePassword,
} from "../../api/clientPortal";
import { PASSWORD_POLICY_CHECKS } from "../../utils/passwordPolicy";
import { getClientPortalErrorMessage } from "./clientPortalErrorMessage";

const RULE_LABELS = {
  len: "Mínimo 8 caracteres",
  upper: "Letra maiúscula",
  num: "Número",
  sym: "Símbolo",
};

export default function ClientPortalPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const company = String(params.get("company") || "").trim().toLowerCase();
  const q = company ? `?company=${encodeURIComponent(company)}` : "";
  const navigate = useNavigate();

  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [checking, setChecking] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [ok, setOk] = useState(false);

  useEffect(() => {
    if (!token) {
      setChecking(false);
      return;
    }
    portalValidatePasswordToken(token, company)
      .then(() => setTokenValid(true))
      .catch(() => setTokenValid(false))
      .finally(() => setChecking(false));
  }, [token, company]);

  const strength = PASSWORD_POLICY_CHECKS.filter((c) => c.test(pwd)).length;
  const maxStrength = PASSWORD_POLICY_CHECKS.length;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (pwd !== confirm) {
      setError("As senhas digitadas não são iguais. Confira os dois campos e tente novamente.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await portalCompletePassword({ token, password: pwd, company });
      setOk(true);
      setTimeout(() => navigate(`/portal-cliente${q}`), 2000);
    } catch (err) {
      setError(getClientPortalErrorMessage(err, { context: "password" }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ClientPortalShell title="Definir senha" subtitle="Crie sua senha de acesso ao portal">
      {checking && <LinearProgress sx={{ mb: 2 }} />}

      {!checking && !tokenValid && (
        <Alert severity="error">
          Este link de senha não é mais válido. Solicite um novo e-mail em “Esqueci minha senha”.
        </Alert>
      )}

      {ok && (
        <Alert severity="success">Senha definida! Redirecionando ao login…</Alert>
      )}

      {tokenValid && !ok && (
        <Stack component="form" spacing={2} onSubmit={handleSubmit}>
          <TextField
            label="Nova senha"
            type={showPwd ? "text" : "password"}
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            required
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPwd((v) => !v)} edge="end">
                    {showPwd ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <LinearProgress variant="determinate" value={(strength / maxStrength) * 100} />
          <Stack spacing={0.5}>
            {PASSWORD_POLICY_CHECKS.map((c) => (
              <Alert
                key={c.id}
                severity={c.test(pwd) ? "success" : "info"}
                icon={false}
                sx={{ py: 0, px: 1, fontSize: 12 }}
              >
                {RULE_LABELS[c.id] || c.id}
              </Alert>
            ))}
          </Stack>
          <TextField
            label="Confirmar senha"
            type={showPwd ? "text" : "password"}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            fullWidth
          />
          {error && <Alert severity="error">{error}</Alert>}
          <Button type="submit" variant="contained" disabled={busy}>
            {busy ? "Salvando…" : "Salvar senha"}
          </Button>
        </Stack>
      )}

      <Button component={RouterLink} to={`/portal-cliente${q}`} sx={{ mt: 2 }} fullWidth>
        Ir para login
      </Button>
    </ClientPortalShell>
  );
}
